/**
 * Maps the employee checklist API response
 * (`/api/proficiency/v2/account/<id>/responsibility/<id>/employee/<id>/checklist/`)
 * into the `levels` shape consumed by `AsEmployeeChecklistInspectionCard`.
 *
 * Kept isolated so the response → view mapping is the only thing to update when
 * the payload evolves. Gated by LaunchDarkly `idea-MDP-452` at the call site.
 */
import { CARD_TYPES } from "../AsEmployeeCheckpointInspectionCard/asEmployeeCheckpointInspectionCardConstants";

const LEVELS = ["Apprentice", "Professional", "Coach", "Master"];

/** Employee MDP-452 checklist accordion — all four readiness tiers. */
export const EMPLOYEE_CHECKLIST_LEVELS = ["Apprentice", "Professional", "Coach", "Master"];

const norm = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

function isQuizRequired(item) {
  const quizMode = norm(item?.active_quiz_mode) || norm(item?.quiz_mode);
  if (quizMode === "skip_quiz") return false;

  if (Array.isArray(item?.activities) && item.activities.length > 0) {
    return item.activities.some((activity) => norm(activity) === "quiz");
  }

  if (item?.has_active_quiz != null) return Boolean(item.has_active_quiz);
  return false;
}

function isInspectionRequired(item) {
  const quizMode = norm(item?.active_quiz_mode) || norm(item?.quiz_mode);
  if (quizMode === "skip_inspection") return false;

  if (Array.isArray(item?.activities) && item.activities.length > 0) {
    return item.activities.some((activity) => norm(activity) === "inspection");
  }

  if (item?.has_active_inspection != null) return Boolean(item.has_active_inspection);
  if (item?.active_has_inspection_protocols != null) {
    return Boolean(item.active_has_inspection_protocols);
  }
  return false;
}

function resolveCardTypeFromActivities(item) {
  if (!Array.isArray(item?.activities) || item.activities.length === 0) return null;

  const acts = item.activities.map(norm);
  const parts = [];
  if (acts.includes("video")) parts.push("Video");
  else if (acts.includes("article")) parts.push("Article");
  if (acts.includes("quiz")) parts.push("Quiz");
  if (acts.includes("inspection")) parts.push("Inspection");

  const type = parts.join(" + ");
  return CARD_TYPES.includes(type) ? type : "Inspector-only";
}

/**
 * Maps UI pass filter to checklist API query params.
 * `GET .../checklist/?type=all` — everything
 * `GET .../checklist/?type=passed` — only fully completed checkpoints
 * `GET .../checklist/?not_yet_passed=true` — incomplete rows (legacy param;
 *   `type` has no not-yet-passed value)
 */
export function resolveChecklistQueryParams(passFilter) {
  if (passFilter === "all") return { type: "all" };
  if (passFilter === "passed") return { type: "passed" };
  return { not_yet_passed: true };
}

export function resolveLevelName(item) {
  const byProficiency = LEVELS[(Number(item?.proficiency_level) || 0) - 1];
  if (byProficiency) return byProficiency;
  const readiness = norm(item?.readiness_level);
  return LEVELS.find((level) => level.toLowerCase() === readiness) || LEVELS[0];
}

export function resolveCardType(item) {
  const fromActivities = resolveCardTypeFromActivities(item);
  if (fromActivities) return fromActivities;

  const media = item?.has_active_video ? "Video" : item?.has_active_article ? "Article" : "";
  const parts = [];
  if (media) parts.push(media);
  if (isQuizRequired(item)) parts.push("Quiz");
  if (isInspectionRequired(item)) parts.push("Inspection");
  const type = parts.join(" + ");
  return CARD_TYPES.includes(type) ? type : "Inspector-only";
}

const MEDIA_DONE = new Set(["passed", "complete", "completed", "watched", "read"]);

function isMediaComplete(item) {
  const statuses = item?.activity_statuses || {};
  if (item?.has_active_video) {
    return MEDIA_DONE.has(norm(statuses.video));
  }
  if (item?.has_active_article) {
    return MEDIA_DONE.has(norm(statuses.article));
  }
  return true;
}

function isQuizPassed(item) {
  return norm(item?.activity_statuses?.quiz) === "passed";
}

function isQuizFailed(item) {
  return norm(item?.activity_statuses?.quiz) === "failed";
}

function isQuizPending(item) {
  if (!isQuizRequired(item)) return false;
  const quizStatus = norm(item?.activity_statuses?.quiz);
  return !["passed", "failed"].includes(quizStatus);
}

function canShowInspectionProgress(item) {
  if (!isInspectionRequired(item)) return false;
  // Not Applicable is set by the inspector and stands even when the learning steps are unfinished.
  if (isNotApplicableStatus(resolveInspectionStatus(item))) return true;
  if (isQuizRequired(item) && !isQuizPassed(item)) return false;
  if ((item?.has_active_video || item?.has_active_article) && !isMediaComplete(item)) {
    return false;
  }
  return true;
}

function isNotApplicableStatus(status) {
  return ["not_applicable", "na", "n/a", "notapplicable"].includes(norm(status));
}

function resolveInspectionStatus(item) {
  return norm(item?.activity_statuses?.inspection || item?.inspection_status);
}

/** True when the checkpoint was marked not applicable (distinct from passed). */
export function isCheckpointNotApplicable(item) {
  if (isNotApplicableStatus(resolveInspectionStatus(item))) return true;
  if (isNotApplicableStatus(norm(item?.overall_status))) return true;
  if (isNotApplicableStatus(norm(item?.inspection_status))) return true;
  if (isNotApplicableStatus(norm(item?.evaluation_status))) return true;
  if (isNotApplicableStatus(norm(item?.status))) return true;
  return false;
}

/**
 * True when a checkpoint was actually passed (excludes Not Applicable).
 * Backend sets `is_passed` for N/A rows; those must not read as passed in card status.
 */
export function isCheckpointPassedForProgress(item) {
  if (isCheckpointNotApplicable(item)) return false;
  return item?.is_passed === true;
}

/**
 * Level/tier counter numerator: passed checkpoints plus Not Applicable.
 * Denominator stays the total active checkpoints in the level (N/A included).
 */
export function isCheckpointCountedAsPassedInLevelCounter(item) {
  if (isCheckpointNotApplicable(item)) return true;
  return item?.is_passed === true;
}

function resolveCheckpointReason(item) {
  const reason = String(item?.reason ?? "").trim();
  return reason || undefined;
}

function chipWithTooltip(chip, reason) {
  if (!reason) return chip;
  return { ...chip, tooltip: reason };
}

function hasAnyActivityProgress(item) {
  const statuses = item?.activity_statuses || {};
  return Object.values(statuses).some((status) => {
    const normalized = norm(status);
    return normalized && !["not_started", "pending", ""].includes(normalized);
  });
}

export function resolveCardStatus(item, type) {
  const overall = norm(item?.overall_status);
  const quizStatus = norm(item?.activity_statuses?.quiz);
  const inspectorOnly = type === "Inspector-only";
  const hasQuiz = /Quiz/.test(type);
  const hasInspection = /Inspection/.test(type);
  const passedStatus =
    inspectorOnly || (!hasQuiz && hasInspection) ? "Inspection passed" : "Complete";

  if (isNotApplicableStatus(resolveInspectionStatus(item)) || isNotApplicableStatus(overall)) {
    return "Not Applicable";
  }

  if (item?.is_passed) return passedStatus;

  if (hasQuiz && isMediaComplete(item) && isQuizPending(item)) {
    return "Quiz not attempted";
  }
  if (hasQuiz && isQuizFailed(item)) {
    return "Quiz failed";
  }

  switch (overall) {
    case "passed":
    case "complete":
    case "completed":
      return passedStatus;
    case "obsolete":
      return "Obsolete";
    case "not_applicable":
    case "na":
      return "Not Applicable";
    case "inspection_failed":
    case "failed_inspection":
      return "Inspection failed";
    case "quiz_failed":
      if (hasQuiz) return "Quiz failed";
      return "Not started";
    case "failed":
      if (hasQuiz && isQuizFailed(item)) return "Quiz failed";
      if (hasInspection && norm(item?.activity_statuses?.inspection) === "failed") {
        return "Inspection failed";
      }
      if (inspectorOnly) return "Inspection failed";
      return "Not started";
    case "inspection_pending":
    case "pending_inspection":
    case "awaiting_inspection":
      if (inspectorOnly) return "Awaiting inspection";
      if (hasQuiz && !isQuizPassed(item)) return "Quiz not attempted";
      return hasQuiz ? "Quiz passed / inspection pending" : "Inspection pending";
    case "in_progress":
    case "started":
      if (hasInspection && norm(item?.activity_statuses?.inspection) === "failed") {
        return "Inspection failed";
      }
      if (hasQuiz && quizStatus === "passed") {
        return hasInspection ? "Quiz passed / inspection pending" : "Complete";
      }
      if (hasQuiz && quizStatus === "failed") return "Quiz failed";
      if (hasQuiz && isMediaComplete(item)) return "Quiz not attempted";
      if (inspectorOnly || hasInspection) {
        return inspectorOnly ? "Awaiting inspection" : "Inspection pending";
      }
      return "Not started";
    case "not_started":
    case "":
    default:
      if (hasQuiz && isMediaComplete(item) && isQuizPending(item)) {
        return "Quiz not attempted";
      }
      return inspectorOnly ? "Awaiting inspection" : "Not started";
  }
}

const INSPECTOR_JUDGMENT_CHIP = {
  label: "Inspector judgment · no learning content",
  variant: "inspector",
};

function isInspectionFailed(item) {
  const overall = norm(item?.overall_status);
  const inspectionStatus = norm(item?.activity_statuses?.inspection);
  return (
    overall === "inspection_failed" ||
    overall === "failed_inspection" ||
    overall === "failed" ||
    inspectionStatus === "failed"
  );
}

export function buildStatusChips(item, type) {
  const inspectionReason = resolveCheckpointReason(item);

  if (type === "Inspector-only") {
    if (isNotApplicableStatus(resolveInspectionStatus(item))) {
      return [
        INSPECTOR_JUDGMENT_CHIP,
        chipWithTooltip({ label: "Not Applicable", variant: "neutral" }, inspectionReason),
      ];
    }
    if (item?.is_passed) {
      return [
        chipWithTooltip({ label: "Inspection passed", variant: "success" }, inspectionReason),
      ];
    }
    if (isInspectionFailed(item)) {
      return [
        INSPECTOR_JUDGMENT_CHIP,
        chipWithTooltip({ label: "Inspection failed", variant: "error" }, inspectionReason),
      ];
    }
    return [INSPECTOR_JUDGMENT_CHIP, { label: "Not yet assessed", variant: "neutral" }];
  }

  const overall = norm(item?.overall_status);
  if (
    !item?.is_passed &&
    (!overall || overall === "not_started") &&
    !hasAnyActivityProgress(item)
  ) {
    return [{ label: "Not started", variant: "neutral" }];
  }

  const chips = [];
  const statuses = item?.activity_statuses || {};

  if (item?.has_active_video || item?.has_active_article) {
    const isVideo = Boolean(item?.has_active_video);
    const mediaStatus = norm(statuses[isVideo ? "video" : "article"]);
    const doneLabel = isVideo ? "Video watched" : "Article read";
    if (MEDIA_DONE.has(mediaStatus)) {
      chips.push({ label: doneLabel, variant: "success" });
    } else if (mediaStatus === "not_applicable") {
      chips.push(
        chipWithTooltip({ label: "Not Applicable", variant: "neutral" }, inspectionReason)
      );
    }
  }

  if (isQuizRequired(item)) {
    const quizStatus = norm(statuses.quiz);
    if (quizStatus === "passed") chips.push({ label: "Quiz passed", variant: "success" });
    else if (quizStatus === "failed") chips.push({ label: "Quiz failed", variant: "error" });
    else if (isMediaComplete(item)) {
      chips.push({ label: "Quiz not attempted", variant: "neutral" });
    }
  }

  if (canShowInspectionProgress(item)) {
    const inspectionStatus = resolveInspectionStatus(item);
    if (inspectionStatus === "passed") {
      chips.push(
        chipWithTooltip({ label: "Inspection passed", variant: "success" }, inspectionReason)
      );
    } else if (inspectionStatus === "failed") {
      chips.push(
        chipWithTooltip({ label: "Inspection failed", variant: "error" }, inspectionReason)
      );
    } else if (isNotApplicableStatus(inspectionStatus)) {
      chips.push(
        chipWithTooltip({ label: "Not Applicable", variant: "neutral" }, inspectionReason)
      );
    } else if (["pending", "not_started", ""].includes(inspectionStatus)) {
      chips.push({ label: "Inspection pending", variant: "warning" });
    }
  }

  if (item?.is_passed && !chips.length) {
    chips.push({ label: "Complete", variant: "success" });
  }
  if (!chips.length) chips.push({ label: "Not started", variant: "neutral" });
  return chips;
}

export function buildCheckpointTitle(item) {
  const text = [item?.prefix, item?.criteria]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function defaultExpandedLevel(groups) {
  const firstIncomplete = groups.find((group) => group.total > 0 && group.passed < group.total);
  return firstIncomplete ? firstIncomplete.level : null;
}

/**
 * @param {Array<object>} items checklist API rows
 * @param {object} [options]
 * @param {"not-yet-passed"|"all"} [options.passFilter]
 * @param {string} [options.search]
 * @param {string|null|undefined} [options.expandedLevel] level to expand;
 *   `undefined` = auto (lowest not-yet-completed), `null` = none expanded.
 * @param {string[]} [options.levels] visible accordion tiers (defaults to all four levels).
 * @returns {Array<object>} level groups for `AsEmployeeChecklistInspectionCard`
 */
export function buildChecklistInspectionLevels(items, options = {}) {
  const {
    passFilter = "not-yet-passed",
    search = "",
    expandedLevel,
    levels = LEVELS,
  } = options;
  const list = Array.isArray(items) ? items : [];
  const query = norm(search);
  const visibleLevels = Array.isArray(levels) && levels.length ? levels : LEVELS;

  const groups = visibleLevels.map((level) => ({
    id: level.toLowerCase(),
    level,
    passed: 0,
    total: 0,
    expanded: false,
    checkpoints: [],
  }));
  const byLevel = groups.reduce((acc, group) => {
    acc[group.level] = group;
    return acc;
  }, {});

  list.forEach((item) => {
    const levelName = resolveLevelName(item);
    const group = byLevel[levelName];
    if (!group) return;

    group.total += 1;
    if (isCheckpointCountedAsPassedInLevelCounter(item)) group.passed += 1;

    if (passFilter === "not-yet-passed" && isCheckpointCountedAsPassedInLevelCounter(item)) {
      return;
    }

    const title = buildCheckpointTitle(item);
    if (query && !title.toLowerCase().includes(query) && !norm(item?.criteria).includes(query)) {
      return;
    }

    const type = resolveCardType(item);
    group.checkpoints.push({
      id: String(item?.evaluation_id ?? item?.id ?? `${group.level}-${group.checkpoints.length}`),
      type,
      status: resolveCardStatus(item, type),
      title,
      statusChips: buildStatusChips(item, type),
      /** Per-facet who/when fields for the badge tooltips (ER-11856). */
      microlearningCompletedOn: item?.microlearning_completed_on ?? null,
      quizAttemptedOn: item?.quiz_attempted_on ?? null,
      inspectedBy: item?.inspected_by ?? null,
      inspectedOn: item?.inspected_on ?? null,
      /** Source row, so card actions can open the checkpoint it came from. */
      item,
    });
  });

  const resolvedExpanded =
    expandedLevel === undefined ? defaultExpandedLevel(groups) : expandedLevel;
  groups.forEach((group) => {
    group.expanded = group.level === resolvedExpanded;
  });

  return groups;
}
